import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box, Container, Divider, Grid, useMediaQuery } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { TooltipIconButton, useIsMounted } from '@paragon/client-tools';
import { LumberList, LumberListPrototype } from '@paragon/contract-library';
import { hasMaterialsEditPermission } from '@paragon/login-tools';
import { TypographyWithActionButtons } from '@paragon/material';
import { getDefaultLumberListGuid, useLumberList } from '@paragon/material-lists';
import { runDelayedSpinner } from '@paragon/task-spinner';

import { setDefaultLumberListGuid } from '../../actions/defaultMaterialSelectionActions';
import {
  archiveLumberList,
  duplicateLumberListPrototype,
  renameLumberList,
  restoreLumberList,
} from '../../actions/lumberListPrototypeActions';
import { createLumberSet } from '../../actions/lumberSetActions';
import { useLumberListPrototype } from '../../selectors/lumberListPrototypeSelectors';
import { UserVisibleError } from '../../UserVisibleError';
import { GRID_SPACING } from '../../util/constants';
import { useAppDispatch, useAppSelector } from '../hooks';
import LumberSetCard from '../interface/cards/LumberSetCard';
import { ListActions } from '../interface/controls/ListActions';
import { LumberListPreview } from '../interface/LumberListPreview';
import { LumberListBreadcrumbs } from '../interface/navigation/LumberListBreadcrumbs';
import { LoadingPage } from './LoadingPage';

export function LumberListPage() {
  const params = useParams();
  const defaultLumberListGuid = useAppSelector(getDefaultLumberListGuid);
  const list = useLumberList(params.lumberListGuid);
  const prototype = useLumberListPrototype(params.lumberListGuid);

  if (prototype === 'loading' || list === 'loading') return <LoadingPage />;

  if (!list || !prototype) throw new UserVisibleError('Lumber List not found.');

  return (
    <UnconnectedLumberListPage
      defaultLumberListGuid={defaultLumberListGuid}
      lumberList={list}
      lumberListPrototype={prototype}
    />
  );
}

export function UnconnectedLumberListPage(props: {
  defaultLumberListGuid: string | null;
  lumberList: LumberList;
  lumberListPrototype: LumberListPrototype;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isMounted = useIsMounted();

  const hasEditPermission = useAppSelector(hasMaterialsEditPermission);

  const hideLumbersInList = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [addingLumberSet, setAddingLumberSet] = useState(false);

  return (
    <Container>
      <LumberListBreadcrumbs lumberList={props.lumberListPrototype} />

      <ListActions
        type="lumber"
        list={props.lumberList}
        defaultListGuid={props.defaultLumberListGuid}
        setDefaultListGuid={setDefaultLumberListGuid}
        archiveList={archiveLumberList}
        restoreList={restoreLumberList}
        duplicateList={duplicateLumberListPrototype}
        renameList={renameLumberList}
      />

      <Divider sx={{ marginBottom: 2 }} />
      <Grid container spacing={GRID_SPACING}>
        <Grid size={{ xs: 12, md: 6, lg: 5 }}>
          {/* TODO: handle if there are no sets defined */}
          <TypographyWithActionButtons text="Lumber Sets" variant="h4" gutterBottom>
            {hasEditPermission && (
              <Box>
                <TooltipIconButton
                  title="Add Lumber Set"
                  disabled={addingLumberSet}
                  onClick={async () => {
                    setAddingLumberSet(true);

                    const spinnerMessage = 'Creating lumber set...';
                    const cleanUpSpinner = dispatch(runDelayedSpinner(spinnerMessage));

                    try {
                      const lumberSetGuid = await dispatch(createLumberSet(props.lumberList.guid));
                      navigate(`/lumberLists/${props.lumberList.guid}/lumberSets/${lumberSetGuid}/edit`);
                    } finally {
                      cleanUpSpinner();
                      if (isMounted()) setAddingLumberSet(false);
                    }
                  }}
                >
                  <AddIcon />
                </TooltipIconButton>
              </Box>
            )}
          </TypographyWithActionButtons>
          {props.lumberListPrototype.lumberSets.map((set) => (
            <LumberSetCard key={set.guid} parentGuid={props.lumberListPrototype.guid} value={set} />
          ))}
        </Grid>

        {hideLumbersInList ? null : (
          <Grid size={{ md: 6, lg: 7 }}>
            <LumberListPreview
              title="Lumbers in List"
              flavorText="This lumber list is composed of all the valid lumbers that match one or more of the lumber sets to the left."
              lumbers={props.lumberList.stockLumbers}
            />
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
